import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, X, Star, Layout, Palette, Globe, Zap, Shield, Mail, ChevronRight, LogIn, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';

const iconMap = {
  layout: Layout,
  palette: Palette,
  globe: Globe,
  zap: Zap,
  shield: Shield,
  mail: Mail,
  star: Star,
};

const LandingPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [processingTier, setProcessingTier] = useState(null);
  const [showDemo, setShowDemo] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchSettings();
    // Capture referral code from URL and store for checkout/registration
    const refCode = searchParams.get('ref');
    if (refCode) {
      localStorage.setItem('nexuscms_ref', refCode);
    }
  }, [searchParams]);

  const fetchSettings = async () => {
    try {
      const res = await api.get('/public/landing-page');
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch landing page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (tier) => {
    setProcessingTier(tier.tier_id);
    try {
      const originUrl = window.location.origin;
      const referralCode = localStorage.getItem('nexuscms_ref') || null;
      const res = await api.post('/payments/create-checkout', {
        tier_id: tier.tier_id,
        billing_period: billingPeriod,
        origin_url: originUrl,
        referral_code: referralCode
      });
      
      // Free tier: no Stripe, just redirect to signup with ?plan=free
      if (res.data.is_free && res.data.redirect_url) {
        window.location.href = res.data.redirect_url;
        return;
      }
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to create checkout:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setProcessingTier(null);
    }
  };

  // Enterprise / custom tiers use "Contact us" instead of self-serve checkout.
  const CONTACT_FALLBACK = 'mailto:sales@nexuscms.ai';
  const handleTierCta = (tier) => {
    if (tier.contact_sales) {
      window.location.href = (tier.cta_url && tier.cta_url !== '#') ? tier.cta_url : CONTACT_FALLBACK;
      return;
    }
    handleCheckout(tier);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Failed to load page content</p>
      </div>
    );
  }

  const { hero = {}, features = [], pricing_tiers = [], testimonials = [], integration_partners = [], enterprise_partners = [], cta = {} } = settings;

  return (
    <div className="min-h-screen">
      {/* Navigation Header */}
      <nav 
        className="fixed top-0 left-0 right-0 z-50 px-4 py-4"
        style={{ 
          backgroundColor: hero?.background_color || '#4F46E5',
        }}
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Layout className="text-white" size={18} />
            </div>
            <span 
              className="text-xl font-bold"
              style={{ color: hero?.text_color || '#ffffff' }}
            >
              Nexus CMS
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 gap-2"
              >
                <LogIn size={18} />
                Login
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-4"
        style={{ 
          backgroundColor: hero.background_color,
          backgroundImage: hero.background_image ? `url(${hero.background_image})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
            style={{ color: hero.text_color }}
            dangerouslySetInnerHTML={{ __html: (hero.headline || '').replace(/&nbsp;/g, '\u00A0') }}
          />
          <p 
            className="text-lg md:text-xl mb-10 max-w-3xl mx-auto opacity-90"
            style={{ color: hero.text_color }}
            dangerouslySetInnerHTML={{ __html: (hero.subheadline || '').replace(/&nbsp;/g, '\u00A0') }}
          />
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="text-lg px-8 py-6 bg-indigo-600 hover:bg-indigo-700"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {hero.cta_primary_text}
              <ChevronRight className="ml-2" size={20} />
            </Button>
            {hero.cta_secondary_text && (
              <Button 
                size="lg" 
                variant="outline"
                className="text-lg px-8 py-6 border-white/30 text-white hover:bg-white/10"
                onClick={() => setShowDemo(true)}
                data-testid="watch-demo-btn"
              >
                <Play className="mr-2 fill-current" size={18} />
                {hero.cta_secondary_text}
              </Button>
            )}
          </div>
        </div>
        {hero.background_image && (
          <div className="absolute inset-0 bg-black/50"></div>
        )}
      </section>

      {/* Features Section */}
      {settings.show_features && features?.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
              Everything you need to succeed
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Powerful features designed to help you build, manage, and grow your online presence.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.sort((a, b) => a.order - b.order).map((feature) => {
                const IconComponent = iconMap[feature.icon] || Star;
                return (
                  <div key={feature.feature_id} className="p-6 rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="text-indigo-600" size={24} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Integration Partners Section */}
      {settings.show_integrations && integration_partners?.length > 0 && (
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
              Integration Partners
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Seamlessly connected with powerful tools and services to extend your CMS capabilities
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {integration_partners.sort((a, b) => a.order - b.order).map((partner) => (
                <a
                  key={partner.partner_id}
                  href={partner.website_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all flex flex-col items-center justify-center text-center border border-slate-100 hover:border-indigo-200"
                >
                  <div className="h-12 w-full flex items-center justify-center mb-3">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name}
                        className="max-h-12 max-w-full object-contain grayscale group-hover:grayscale-0 transition-all"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl items-center justify-center text-white font-bold text-xl"
                      style={{ display: partner.logo_url ? 'none' : 'flex' }}
                    >
                      {partner.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <h3 className="font-semibold text-slate-900 text-sm group-hover:text-indigo-600 transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {partner.description}
                  </p>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Enterprise Partners Section */}
      {settings.show_enterprise_partners && enterprise_partners?.length > 0 && (
        <section className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
              Enterprise Partners
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Trusted by leading companies to deliver exceptional digital experiences
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {enterprise_partners.sort((a, b) => a.order - b.order).map((partner) => (
                <a
                  key={partner.partner_id}
                  href={partner.website_url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 hover:shadow-xl transition-all border border-slate-200 hover:border-indigo-300 text-center"
                >
                  <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    {partner.logo_url ? (
                      <img 
                        src={partner.logo_url} 
                        alt={partner.name}
                        className="w-12 h-12 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                    ) : null}
                    <span 
                      className="text-white font-bold text-3xl"
                      style={{ display: partner.logo_url ? 'none' : 'block' }}
                    >
                      {partner.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    {partner.name}
                  </h3>
                  <p className="text-slate-600">
                    {partner.description}
                  </p>
                  <div className="mt-4 inline-flex items-center text-indigo-600 font-medium text-sm group-hover:gap-2 transition-all">
                    Learn more
                    <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {settings.show_pricing && pricing_tiers?.length > 0 && (
        <section id="pricing" className="py-20 px-4 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-slate-600 text-center mb-8">
              Choose the plan that's right for you
            </p>
            
            {/* Billing Toggle */}
            <div className="flex justify-center items-center gap-4 mb-12">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  billingPeriod === 'monthly' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  billingPeriod === 'yearly' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  Save 20%
                </span>
              </button>
            </div>

            <div className={`grid gap-8 ${
              pricing_tiers.length === 1 ? 'md:max-w-md md:mx-auto' :
              pricing_tiers.length === 2 ? 'md:grid-cols-2 md:max-w-3xl md:mx-auto' :
              pricing_tiers.length === 3 ? 'md:grid-cols-3' :
              'md:grid-cols-2 lg:grid-cols-4'
            }`}>
              {pricing_tiers.sort((a, b) => a.order - b.order).map((tier) => {
                const isFreeTier = tier.tier_key === 'free' || (Number(tier.price_monthly) === 0 && Number(tier.price_yearly) === 0);
                return (
                <div 
                  key={tier.tier_id}
                  className={`relative rounded-2xl p-8 ${
                    tier.is_popular 
                      ? 'bg-indigo-600 text-white ring-4 ring-indigo-600 ring-offset-4' 
                      : 'bg-white border-2 border-slate-200'
                  }`}
                >
                  {tier.is_popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-amber-400 to-orange-500 text-white text-sm font-medium px-4 py-1 rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <h3 className={`text-2xl font-bold mb-2 ${tier.is_popular ? 'text-white' : 'text-slate-900'}`}>
                    {tier.name}
                  </h3>
                  <p className={`mb-6 ${tier.is_popular ? 'text-indigo-100' : 'text-slate-600'}`}>
                    {tier.description}
                  </p>
                  <div className="mb-6">
                    {tier.contact_sales ? (
                      <span className={`text-4xl font-bold ${tier.is_popular ? 'text-white' : 'text-slate-900'}`}>
                        Contact us
                      </span>
                    ) : isFreeTier ? (
                      <span className={`text-4xl font-bold ${tier.is_popular ? 'text-white' : 'text-slate-900'}`}>
                        Free
                      </span>
                    ) : (
                      <>
                        <span className={`text-4xl font-bold ${tier.is_popular ? 'text-white' : 'text-slate-900'}`}>
                          ${billingPeriod === 'yearly' ? tier.price_yearly : tier.price_monthly}
                        </span>
                        <span className={tier.is_popular ? 'text-indigo-100' : 'text-slate-500'}>
                          /{billingPeriod === 'yearly' ? 'year' : 'month'}
                        </span>
                      </>
                    )}
                  </div>
                  <ul className="space-y-3 mb-8">
                    {tier.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-3">
                        {feature.included ? (
                          <Check className={`flex-shrink-0 ${tier.is_popular ? 'text-indigo-200' : 'text-green-500'}`} size={20} />
                        ) : (
                          <X className={`flex-shrink-0 ${tier.is_popular ? 'text-indigo-300' : 'text-slate-300'}`} size={20} />
                        )}
                        <span className={feature.included ? '' : 'opacity-60'}>
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleTierCta(tier)}
                    disabled={processingTier === tier.tier_id && !tier.contact_sales}
                    className={`w-full py-6 text-lg font-semibold ${
                      tier.is_popular 
                        ? 'bg-white text-indigo-600 hover:bg-indigo-50' 
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    }`}
                  >
                    {tier.contact_sales
                      ? (tier.cta_text || 'Contact Sales')
                      : (processingTier === tier.tier_id ? 'Processing...' : tier.cta_text)}
                  </Button>
                </div>
                );
              })}
            </div>

            {/* Feature comparison table. comparison_mode 'custom' renders the
                admin-edited matrix (comparison_rows) verbatim; otherwise it auto-
                derives ✓/✗ from each tier's feature list. Hidden on phones. */}
            {settings.show_comparison !== false && pricing_tiers.length > 1 && (() => {
              const sortedTiers = pricing_tiers.slice().sort((a, b) => a.order - b.order);
              const useCustom = settings.comparison_mode === 'custom'
                && Array.isArray(settings.comparison_rows)
                && settings.comparison_rows.length > 0;

              let rows;
              if (useCustom) {
                rows = settings.comparison_rows
                  .filter((r) => (r.label || '').trim())
                  .map((r) => ({
                    label: r.label,
                    get: (t) => (r.values && r.values[t.tier_id] != null) ? r.values[t.tier_id] : '',
                  }));
              } else {
                const seen = new Map(); // normalized key -> display label
                sortedTiers.forEach((t) => {
                  (t.features || []).forEach((f) => {
                    const label = (f.text || '').trim();
                    const key = label.toLowerCase();
                    if (key && !seen.has(key)) seen.set(key, label);
                  });
                });
                const includesFeature = (t, key) =>
                  (t.features || []).some((f) => (f.text || '').trim().toLowerCase() === key && f.included);
                rows = Array.from(seen.entries())
                  .map(([key, label]) => ({
                    label,
                    get: (t) => (includesFeature(t, key) ? 'yes' : 'no'),
                  }))
                  // Drop rows that are ✗ for EVERY tier — these are noise caused by
                  // tiers using different wording for the same feature.
                  .filter((row) => sortedTiers.some((t) => row.get(t) === 'yes'));
              }
              if (rows.length === 0) return null;

              const renderCell = (raw) => {
                const v = (raw == null ? '' : String(raw)).trim();
                const low = v.toLowerCase();
                if (['yes', 'true', '✓', 'included', 'y', '1'].includes(low)) {
                  return <Check className="inline text-green-500" size={18} />;
                }
                if (['no', 'false', '-', '', 'x', '✗', 'n', '0'].includes(low)) {
                  return <X className="inline text-slate-300" size={18} />;
                }
                return <span className="text-slate-700 font-medium">{v}</span>;
              };

              return (
                <div className="hidden md:block mt-16">
                  <h3 className="text-2xl font-bold text-center text-slate-900 mb-2">Compare plans</h3>
                  <p className="text-center text-slate-600 mb-8">All the features, side-by-side.</p>
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left py-4 px-6 font-semibold text-slate-700 w-1/3">Feature</th>
                          {sortedTiers.map((t) => {
                            const isFreeTier = t.tier_key === 'free' || (Number(t.price_monthly) === 0 && Number(t.price_yearly) === 0);
                            return (
                              <th key={t.tier_id} className={`text-center py-4 px-4 font-semibold ${t.is_popular ? 'text-indigo-600' : 'text-slate-700'}`}>
                                <div className="flex flex-col items-center gap-1">
                                  <span>{t.name}</span>
                                  <span className="text-xs font-normal text-slate-500">
                                    {t.contact_sales ? 'Contact us'
                                      : isFreeTier ? 'Free'
                                      : `$${billingPeriod === 'yearly' ? t.price_yearly : t.price_monthly}/${billingPeriod === 'yearly' ? 'yr' : 'mo'}`}
                                  </span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, rowIdx) => (
                          <tr key={`${row.label}-${rowIdx}`} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="py-3 px-6 text-slate-700">{row.label}</td>
                            {sortedTiers.map((t) => (
                              <td key={t.tier_id} className="text-center py-3 px-4">
                                {renderCell(row.get(t))}
                              </td>
                            ))}
                          </tr>
                        ))}
                        {/* Final CTA row */}
                        <tr className="bg-slate-50 border-t border-slate-200">
                          <td className="py-4 px-6 font-medium text-slate-700">Get started</td>
                          {sortedTiers.map((t) => (
                            <td key={t.tier_id} className="text-center py-4 px-4">
                              <Button
                                onClick={() => handleTierCta(t)}
                                disabled={processingTier === t.tier_id && !t.contact_sales}
                                className={`text-xs px-3 py-1 h-auto ${t.is_popular ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-white border border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                              >
                                {t.contact_sales ? (t.cta_text || 'Contact Sales') : (processingTier === t.tier_id ? '...' : t.cta_text)}
                              </Button>
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {settings.show_testimonials && testimonials?.length > 0 && (
        <section className="py-20 px-4 bg-slate-50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-4">
              Loved by teams worldwide
            </h2>
            <p className="text-lg text-slate-600 text-center mb-12">
              See what our customers have to say
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              {testimonials.sort((a, b) => a.order - b.order).map((testimonial) => (
                <div key={testimonial.testimonial_id} className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-amber-400 fill-amber-400" size={18} />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    {testimonial.author_image ? (
                      <img 
                        src={testimonial.author_image} 
                        alt={testimonial.author_name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-indigo-600 font-semibold text-lg">
                          {testimonial.author_name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">{testimonial.author_name}</p>
                      <p className="text-sm text-slate-500">
                        {testimonial.author_title}
                        {testimonial.author_company && `, ${testimonial.author_company}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {settings.show_cta && cta && (
        <section 
          className="py-20 px-4"
          style={{ backgroundColor: cta.background_color }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: cta.text_color }}
              dangerouslySetInnerHTML={{ __html: (cta.headline || '').replace(/&nbsp;/g, '\u00A0') }}
            />
            <p 
              className="text-lg mb-8 opacity-90"
              style={{ color: cta.text_color }}
              dangerouslySetInnerHTML={{ __html: (cta.subheadline || '').replace(/&nbsp;/g, '\u00A0') }}
            />
            <Button 
              size="lg"
              className="text-lg px-8 py-6 bg-white text-indigo-600 hover:bg-slate-100"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {cta.cta_text}
              <ChevronRight className="ml-2" size={20} />
            </Button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-8 px-4 bg-slate-900">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} Nexus CMS. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Watch Demo video modal */}
      {showDemo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in"
          onClick={() => setShowDemo(false)}
          data-testid="demo-video-overlay"
        >
          <div
            className="relative w-full max-w-5xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute -top-12 right-0 text-white/80 hover:text-white flex items-center gap-2 text-sm"
              onClick={() => setShowDemo(false)}
              data-testid="demo-video-close"
            >
              Close <X size={22} />
            </button>
            <div className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
              <video
                src={hero.demo_video_url || '/demo/nexus-demo.mp4'}
                poster="/demo/nexus-demo-poster.png"
                controls
                autoPlay
                muted
                playsInline
                preload="auto"
                className="w-full h-auto block"
                data-testid="demo-video-player"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
